import { useEffect, useRef } from 'react'

// Everything below is deterministic (no Math.random) and driven purely by
// elapsed time, layering several sine terms with incommensurate, slowly
// drifting frequencies. That combination never meaningfully repeats on any
// timescale a viewer would notice, so the line reads as continuously "new"
// without needing a randomizer or any client/server hydration gymnastics.

// Slow rise/fall in overall amplitude, like real audio dynamics (louder and
// quieter passages), with a drifting phase so the swell period itself varies.
function envelopeAt(t: number) {
	return 0.65 + 0.35 * Math.sin(t * 0.05 + Math.sin(t * 0.011) * 2.5)
}

// Gentle overall brightness pulse — the "electric arc flicker".
function flickerAt(t: number) {
	return 0.9 + 0.1 * Math.sin(t * 0.9 + Math.sin(t * 0.17) * 2)
}

// Broad, slowly scrolling shape (gives the line a sense of traveling left,
// like a scope trace) plus dense high-frequency jaggedness (a cheap
// value-noise stand-in for the "zoomed way into a waveform" texture) —
// combined and scaled to mostly stay within the band, with rare bigger
// peaks where the layers happen to line up, same as real audio.
function sampleAt(x: number, t: number) {
	const mid =
		Math.sin(x * 0.01 - t * 0.6) +
		0.6 * Math.sin(x * 0.023 + t * 0.37) +
		0.8 * Math.sin(x * 0.007 - t * 0.21 + 1.3)
	const jagged =
		Math.sin(x * 0.37 + t * 2.1) +
		0.6 * Math.sin(x * 0.91 - t * 3.53 + 0.7) +
		0.35 * Math.sin(x * 1.7 + t * 1.29 + 2.1) +
		0.2 * Math.sin(x * 3.3 - t * 0.83)
	return mid * 0.3 + jagged * 0.15
}

const SAMPLES_PER_PIXEL = 3
const BAND_HEIGHT_FRACTION = 0.1

export default function AmbientWaveformBackground() {
	const canvasRef = useRef<HTMLCanvasElement>(null)

	useEffect(() => {
		const canvas = canvasRef.current
		const ctx = canvas?.getContext('2d')
		if (!canvas || !ctx) return

		const dpr = Math.min(window.devicePixelRatio || 1, 2)
		let width = 0
		let height = 0

		function resize() {
			width = window.innerWidth
			height = window.innerHeight
			canvas!.width = Math.floor(width * dpr)
			canvas!.height = Math.floor(height * dpr)
			canvas!.style.width = `${width}px`
			canvas!.style.height = `${height}px`
			ctx!.setTransform(dpr, 0, 0, dpr, 0, 0)
		}
		resize()
		window.addEventListener('resize', resize)

		function draw(nowMs: number) {
			const t = nowMs / 1000
			ctx!.clearRect(0, 0, width, height)

			const bandHeight = height * BAND_HEIGHT_FRACTION
			const centerY = height / 2
			const envelope = envelopeAt(t)
			const flicker = flickerAt(t)
			const step = 1 / SAMPLES_PER_PIXEL

			ctx!.beginPath()
			for (let x = 0, first = true; x <= width; x += step, first = false) {
				const y = centerY + envelope * sampleAt(x, t) * bandHeight
				if (first) ctx!.moveTo(x, y)
				else ctx!.lineTo(x, y)
			}

			// Outer glow pass — soft, wide, low-alpha halo. Kept faint: this sits
			// behind readable text on every page, so it must lose the contrast
			// fight, not win it.
			ctx!.save()
			ctx!.globalAlpha = 0.09 * flicker
			ctx!.strokeStyle = 'rgba(139, 92, 246, 1)'
			ctx!.lineWidth = 6
			ctx!.shadowBlur = 18
			ctx!.shadowColor = 'rgba(139, 92, 246, 0.9)'
			ctx!.stroke()
			ctx!.restore()

			// Inner core pass — thin, bright, crisp line reading as the "wire".
			ctx!.save()
			ctx!.globalAlpha = 0.22 * flicker
			ctx!.strokeStyle = 'rgba(230, 220, 255, 1)'
			ctx!.lineWidth = 1.1
			ctx!.shadowBlur = 6
			ctx!.shadowColor = 'rgba(199, 168, 255, 0.9)'
			ctx!.stroke()
			ctx!.restore()
		}

		const prefersReducedMotion = window.matchMedia(
			'(prefers-reduced-motion: reduce)',
		).matches

		let rafId = 0
		if (prefersReducedMotion) {
			draw(0)
		} else {
			const loop = (now: number) => {
				draw(now)
				rafId = requestAnimationFrame(loop)
			}
			rafId = requestAnimationFrame(loop)
		}

		return () => {
			window.removeEventListener('resize', resize)
			if (rafId) cancelAnimationFrame(rafId)
		}
	}, [])

	return (
		<div
			aria-hidden="true"
			className="pointer-events-none fixed inset-0 z-0 overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_12%,black_88%,transparent)] [-webkit-mask-image:linear-gradient(to_right,transparent,black_12%,black_88%,transparent)]"
		>
			<canvas ref={canvasRef} className="h-full w-full" />
		</div>
	)
}
